import { type ComponentProps, useId } from "react";
import { Input } from "./ui/input";

export function ReviewInput({
	error,
	label,
	...props
}: ComponentProps<typeof Input> & { error?: string; label: string }) {
	const errorId = useId();
	return (
		<>
			<Input
				{...props}
				aria-label={label}
				aria-invalid={Boolean(error)}
				aria-describedby={error ? errorId : undefined}
			/>
			{error && (
				<span id={errorId} className="mt-1 block text-xs text-red-700">
					{error}
				</span>
			)}
		</>
	);
}

type Mark = { type: string; reason?: string };
export function markReasonError(mark: Mark) {
	if (mark.type !== "other_sensitive_content") return undefined;
	return !mark.reason?.trim()
		? "Enter a reason for other sensitive content."
		: mark.reason.length > 500
			? "Use 500 characters or fewer."
			: undefined;
}

export function reviewMarkErrors(
	text: string,
	textMarks: Array<Mark & { start: number; end: number }>,
	audioMarks: Array<Mark & { startSec: number; endSec: number }>,
	duration: number,
) {
	const errors: Record<string, string> = {};
	textMarks.forEach((mark, index) => {
		if (
			!Number.isInteger(mark.start) ||
			mark.start < 0 ||
			mark.start >= text.length
		)
			errors[`text-${index}-start`] =
				"Enter a whole-number position within the transcript.";
		if (
			!Number.isInteger(mark.end) ||
			mark.end <= mark.start ||
			mark.end > text.length
		)
			errors[`text-${index}-end`] =
				"Enter a whole-number end after the start, within the transcript.";
		const reason = markReasonError(mark);
		if (reason) errors[`text-${index}-reason`] = reason;
	});
	audioMarks.forEach((mark, index) => {
		if (!Number.isFinite(mark.startSec) || mark.startSec < 0)
			errors[`audio-${index}-start`] =
				"Enter a start of zero seconds or later.";
		if (
			!Number.isFinite(mark.endSec) ||
			mark.endSec <= mark.startSec ||
			mark.endSec > Math.min(duration || 14400, 14400)
		)
			errors[`audio-${index}-end`] =
				"Enter an end after the start, within the recording.";
		const reason = markReasonError(mark);
		if (reason) errors[`audio-${index}-reason`] = reason;
	});
	return errors;
}
